<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import VideoListComponent from '@/components/VideoList.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { Lecture } from '@/types/lecture.types'
import { defineEmits, defineProps, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const isMobile = useIsMobile()
const router = useRouter()

const props = defineProps<{
  isVisible: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const close = () => {
  emit('close')
}

const searchQuery = ref('')
const isSearching = ref(false)
const isLoading = ref(false)
const lectureCursorInfo = ref<ApiCursorBody<Lecture>>()
const videoList = ref<Lecture[]>([])
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

const handleSearch = () => {
  search()
}

const search = async (isLoadMore = false) => {
  if (isLoading.value) return

  isLoading.value = true
  isSearching.value = true

  try {
    const lectureCursor = await lectureService.getLectures({
      searchKeyword: searchQuery.value,
      size: 18,
      cursor: isLoadMore ? lectureCursorInfo.value?.nextCursor : undefined,
    })

    if (isLoadMore) {
      videoList.value = [...videoList.value, ...lectureCursor.data]
    } else {
      videoList.value = lectureCursor.data
    }

    lectureCursorInfo.value = lectureCursor
  } catch (error) {
    console.error('검색 중 오류가 발생했습니다:', error)
  } finally {
    isLoading.value = false
  }
}

const reset = () => {
  searchQuery.value = ''
  videoList.value = []
  isSearching.value = false
  lectureCursorInfo.value = undefined
}

const handleVideoClick = (videoId: number) => {
  close()
  reset()
  router.push(`/video/${videoId}`)
}

const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && lectureCursorInfo.value?.hasNext && !isLoading.value) {
        search(true)
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

onMounted(() => {
  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <div v-if="props.isVisible" class="fullscreen-search">
    <div class="search-header">
      <img
        class="close-button svg-white"
        src="@/assets/svgs/close.svg"
        alt="close"
        @click="close"
      />
    </div>
    <div class="search-content">
      <div class="search-input">
        <input
          class="search-input-field title-lg white"
          :class="{ 'title-sm': isMobile }"
          type="text"
          v-model="searchQuery"
          placeholder="검색어 입력 후 엔터를 눌러주세요"
          @keydown.enter.prevent="handleSearch"
        />
        <div v-if="searchQuery.length > 0" class="reset-button svg-white" @click="reset">
          <img class="reset-button-icon" src="@/assets/svgs/close.svg" alt="reset" />
        </div>
      </div>
      <div class="search-divider"></div>
    </div>
    <div v-if="isSearching" class="search-result-list-section">
      <VideoListComponent
        class="search-results"
        :videos="videoList"
        :gridColumns="3"
        @videoClick="handleVideoClick"
      />
      <!-- 로딩 트리거 요소 -->
      <div ref="loadMoreTrigger" class="load-more-trigger">
        <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fullscreen-search {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--gray-900);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  overflow-y: auto;
  padding-bottom: 100px;
}
.search-header {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  top: 40px;
  right: 40px;
}
.close-button {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  cursor: pointer;
}
.search-content {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 140px;
}

.search-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 1280px;
}

.reset-button-icon {
  width: 100%;
  height: 100%;
}

.search-input-field {
  width: 1280px;
  transition: color 0.3s;
  height: auto;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid var(--gray-500);
}

.reset-button {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  cursor: pointer;
}

.search-input-field::placeholder {
  color: var(--gray-800);
}

.search-input-field:focus {
  outline: none;
}

.search-input:hover {
  color: #ccc;
}

.search-divider {
  width: 1280px;
  height: 1px;
  background-color: var(--gray-800);
}

.search-results {
  margin-top: 28px;
  width: 1280px;
}

.load-more-trigger {
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.loading-spinner {
  color: var(--gray-600);
  font-size: 14px;
}

@media (max-width: 1365px) {
  .fullscreen-search {
    left: 0;
    right: 0;
    align-items: center;
    justify-content: start;
    max-width: 600px;
    margin: 0 auto;
  }
  .search-header {
    top: 12px;
    right: 20px;
  }
  .close-button {
    width: 24px;
    height: 24px;
  }
  .search-content {
    position: relative;
    top: 0px;
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 68px;
    padding: 0 20px;
  }
  .search-input {
    width: 100%;
  }
  .search-input-field {
    width: 100%;
  }
  .search-results {
    width: 100%;
    padding: 0px;
    margin-top: 12px;
  }

  .search-divider {
    width: 100%;
  }

  .reset-button {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    aspect-ratio: 1/1;
    cursor: pointer;
  }

  .search-result-list-section {
    width: 100%;
    padding: 0px;
    margin-top: 12px;
  }
}
</style>
